import React, { useState, useRef, useEffect } from 'react';
import {
  Bot,
  Send,
  User,
  Loader2,
  Sparkles,
  RotateCcw,
  ChevronDown,
} from 'lucide-react';
import api from '../services/api';

// ─── Suggested questions ──────────────────────────────────────────────────────
const SUGGESTIONS = [
  'List all active students',
  'How much does Rashmi owe us?',
  'Show all rooms and their current occupancy',
  'Which students have unpaid dues?',
  'How long has Kavya been with us?',
  'Show all payments made this month',
  'How many rooms are available right now?',
  'List students from Colombo University',
];

// ─── Message bubble ───────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow
          ${isUser ? 'bg-brand-600' : 'bg-gradient-to-br from-violet-500 to-purple-700'}`}
      >
        {isUser ? <User size={14} /> : <Bot size={14} />}
      </div>

      {/* Bubble */}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm text-sm leading-relaxed whitespace-pre-wrap
          ${isUser
            ? 'bg-brand-600 text-white rounded-tr-sm'
            : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
          }`}
      >
        {msg.content}
      </div>
    </div>
  );
}

// ─── Typing indicator ─────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-violet-500 to-purple-700 text-white shadow">
        <Bot size={14} />
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="flex gap-1 items-center h-4">
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
          <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  );
}

// ─── Main AiChat page ─────────────────────────────────────────────────────────
export default function AiChat() {
  const [messages, setMessages] = useState([]); // display messages (role: user | assistant)
  const [apiHistory, setApiHistory] = useState([]); // OpenAI message history (includes tool messages)
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Show scroll-to-bottom button when scrolled up
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distFromBottom > 200);
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [input]);

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setInput('');
    setError('');
    setShowSuggestions(false);
    setLoading(true);

    const userMsg = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMsg]);

    try {
      const { data } = await api.post('/ai/chat', {
        message: trimmed,
        history: apiHistory,
      });

      const assistantMsg = { role: 'assistant', content: data.reply };
      setMessages(prev => [...prev, assistantMsg]);
      setApiHistory(data.updatedHistory || []);
    } catch (err) {
      const errText =
        err.response?.data?.error ||
        'Something went wrong. Please try again.';
      setError(errText);
      // Remove the optimistic user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
      // Re-focus the textarea
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([]);
    setApiHistory([]);
    setError('');
    setShowSuggestions(true);
    textareaRef.current?.focus();
  };

  const isEmpty = messages.length === 0;

  return (
    <div
      className="flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm"
      style={{ height: 'calc(100vh - 7rem)' }}
    >

      {/* ── Header ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow">
            <Sparkles size={18} className="text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-gray-900 text-base">Eden Crest AI Assistant</h1>
            <p className="text-xs text-gray-500">Ask anything about your hostel data</p>
          </div>
        </div>

        {!isEmpty && (
          <button
            onClick={resetChat}
            title="Start new conversation"
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 hover:border-gray-300 rounded-lg px-3 py-1.5 transition-colors"
          >
            <RotateCcw size={13} />
            New chat
          </button>
        )}
      </div>

      {/* ── Messages area ── */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-5 relative"
        style={{ minHeight: 0 }}
      >
        {isEmpty ? (
          /* ── Welcome / Empty state ── */
          <div className="flex flex-col items-center justify-center h-full text-center px-4 pb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg mb-4">
              <Bot size={32} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Hello, Admin! 👋</h2>
            <p className="text-gray-500 text-sm mb-8 max-w-sm">
              Ask me anything about the hostel — list students, check balances, filter payments,
              count rooms, search by university, or any other data question.
            </p>

            {/* Suggestions */}
            <div className="w-full max-w-lg">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Try asking…
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-left text-sm bg-white hover:bg-violet-50 border border-gray-200 hover:border-violet-300 text-gray-700 hover:text-violet-700 rounded-xl px-4 py-3 transition-all shadow-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ── Chat messages ── */
          <>
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}
            {loading && <TypingIndicator />}
            {error && (
              <div className="flex justify-center">
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 text-sm max-w-sm text-center">
                  ⚠️ {error}
                </div>
              </div>
            )}
          </>
        )}
        {/* Scroll to bottom button */}
        {showScrollBtn && !isEmpty && (
          <div className="sticky bottom-4 flex justify-end pr-2">
            <button
              onClick={scrollToBottom}
              className="bg-white border border-gray-200 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow text-gray-500 hover:text-gray-800"
            >
              <ChevronDown size={18} />
            </button>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Input area ── */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3">
        {/* Inline suggestions (shown mid-chat as chips) */}
        {!isEmpty && showSuggestions && (
          <div className="flex gap-2 flex-wrap mb-2">
            {SUGGESTIONS.slice(0, 3).map((s, i) => (
              <button
                key={i}
                onClick={() => sendMessage(s)}
                className="text-xs bg-gray-100 hover:bg-violet-100 text-gray-600 hover:text-violet-700 rounded-full px-3 py-1 transition-colors border border-transparent hover:border-violet-200"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2 bg-gray-100 rounded-2xl px-4 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-violet-400 focus-within:shadow transition-all">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(false)}
            placeholder="Ask anything — list students, check dues, filter payments, search rooms…"
            disabled={loading}
            className="flex-1 bg-transparent resize-none outline-none text-sm text-gray-800 placeholder-gray-400 py-1 disabled:opacity-50"
            style={{ maxHeight: 160 }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            title="Send (Enter)"
            className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl transition-all
              ${loading || !input.trim()
                ? 'text-gray-400 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700 text-white shadow'
              }`}
          >
            {loading ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Send size={15} />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-center">
          Press <kbd className="bg-gray-100 px-1 py-0.5 rounded text-xs">Enter</kbd> to send · <kbd className="bg-gray-100 px-1 py-0.5 rounded text-xs">Shift+Enter</kbd> for new line
        </p>
      </div>
    </div>
  );
}
